import PropTypes from "prop-types";
import { useEffect, useRef } from "react";

const CLOSE_VERTEX_THRESHOLD_PX = 16;
const EQUATOR_METERS_PER_PIXEL = 156543.03392;

const isSameLatLng = (firstPoint, secondPoint) => {
  return (
    firstPoint &&
    secondPoint &&
    firstPoint.lat() === secondPoint.lat() &&
    firstPoint.lng() === secondPoint.lng()
  );
};

const getMetersPerPixel = (latLng, zoom) => {
  return (
    (EQUATOR_METERS_PER_PIXEL * Math.cos((latLng.lat() * Math.PI) / 180)) /
    Math.pow(2, zoom)
  );
};

function PolygonDrawing({ map, options, onPolygonComplete }) {
  const verticesRef = useRef([]);
  const polygonRef = useRef(null);
  const previewRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return undefined;

    const maps = window.google.maps;
    const polygonOptions = (options && options.polygonOptions) || {};
    const previousDblClickZoom = map.get("disableDoubleClickZoom");
    map.setOptions({ disableDoubleClickZoom: true });

    const createPolygon = (paths, additionalOptions = {}) => {
      return new maps.Polygon({
        ...polygonOptions,
        ...additionalOptions,
        clickable: false,
        paths
      });
    };

    const clearPreview = () => {
      if (previewRef.current) {
        previewRef.current.setMap(null);
        previewRef.current = null;
      }
    };

    const resetDrawing = () => {
      verticesRef.current = [];
      polygonRef.current = null;
      clearPreview();
    };

    const refreshPreview = () => {
      clearPreview();
      if (verticesRef.current.length < 2) return;

      previewRef.current = createPolygon(verticesRef.current, {
        editable: false
      });
      previewRef.current.setMap(map);
    };

    const commitPolygon = () => {
      if (polygonRef.current || verticesRef.current.length < 3) return;

      clearPreview();
      polygonRef.current = createPolygon(verticesRef.current);
      polygonRef.current.setMap(map);
      onPolygonComplete(polygonRef.current);
    };

    const refreshPolygon = () => {
      if (polygonRef.current) {
        polygonRef.current.setPath(verticesRef.current);
        return;
      }

      refreshPreview();
      commitPolygon();
    };

    const getCloseVertexThreshold = latLng => {
      const zoom = map.getZoom() || 18;
      const metersPerPixel = getMetersPerPixel(latLng, zoom);

      return Math.max(metersPerPixel * CLOSE_VERTEX_THRESHOLD_PX, 0.5);
    };

    const isClosingFirstVertex = latLng => {
      const firstPoint = verticesRef.current[0];

      if (
        verticesRef.current.length < 3 ||
        !maps.geometry ||
        !maps.geometry.spherical ||
        !firstPoint
      ) {
        return false;
      }

      const distance = maps.geometry.spherical.computeDistanceBetween(
        latLng,
        firstPoint
      );

      return distance <= getCloseVertexThreshold(latLng);
    };

    const isDuplicateLastVertex = () => {
      const points = verticesRef.current;
      const lastPoint = points[points.length - 1];
      const previousPoint = points[points.length - 2];

      if (isSameLatLng(lastPoint, previousPoint)) {
        return true;
      }

      if (
        !lastPoint ||
        !previousPoint ||
        !maps.geometry ||
        !maps.geometry.spherical
      ) {
        return false;
      }

      return (
        maps.geometry.spherical.computeDistanceBetween(
          lastPoint,
          previousPoint
        ) <= getCloseVertexThreshold(lastPoint)
      );
    };

    const getFinishedPoints = () => {
      const points = verticesRef.current;
      const firstPoint = points[0];
      const lastPoint = points[points.length - 1];

      if (isSameLatLng(firstPoint, lastPoint)) {
        return points.slice(0, -1);
      }

      if (isDuplicateLastVertex()) {
        return points.slice(0, -1);
      }

      return points;
    };

    const finishPolygon = () => {
      const points = getFinishedPoints();
      clearPreview();
      if (points.length < 3) return;

      verticesRef.current = points;

      if (polygonRef.current) {
        polygonRef.current.setPath(points);
        resetDrawing();
        return;
      }

      const polygon = createPolygon(points);
      polygon.setMap(map);
      onPolygonComplete(polygon);
      resetDrawing();
    };

    const clickListener = map.addListener("click", event => {
      const { latLng } = event;

      if (!latLng) return;

      if (isClosingFirstVertex(latLng)) {
        finishPolygon();
        return;
      }

      verticesRef.current = [...verticesRef.current, latLng];
      refreshPolygon();
    });

    const dblClickListener = map.addListener("dblclick", () => {
      finishPolygon();
    });

    return () => {
      maps.event.removeListener(clickListener);
      maps.event.removeListener(dblClickListener);
      verticesRef.current = [];
      polygonRef.current = null;
      clearPreview();
      map.setOptions({ disableDoubleClickZoom: previousDblClickZoom });
    };
  }, [map, options, onPolygonComplete]);

  return null;
}

PolygonDrawing.propTypes = {
  map: PropTypes.object.isRequired,
  onPolygonComplete: PropTypes.func,
  options: PropTypes.object
};

PolygonDrawing.defaultProps = {
  onPolygonComplete: () => {},
  options: {}
};

export default PolygonDrawing;
